import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Menu, X, Globe, Phone, Mail } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: { en: 'Home', ar: 'الرئيسية' }, path: '/' },
    { label: { en: 'Properties', ar: 'العقارات' }, path: '/properties' },
    { label: { en: 'Services', ar: 'الخدمات' }, path: '/services' },
    { label: { en: 'Blog', ar: 'المدونة' }, path: '/blog' },
    { label: { en: 'About', ar: 'من نحن' }, path: '/about' },
    { label: { en: 'Contact', ar: 'اتصل بنا' }, path: '/contact' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/98 backdrop-blur-md shadow-xl' : 'bg-white/90 backdrop-blur-lg shadow-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_netgains-redesign/artifacts/97d5giqr_logo-vectorfile%20%283%29.png" 
              alt="Net Gains Real Estate" 
              width="220"
              height="72"
              className="h-16 md:h-[72px] w-auto object-contain transition-all duration-300 group-hover:opacity-90"
              style={{ imageRendering: 'crisp-edges' }}
            />
            <div className="hidden sm:flex flex-col justify-center leading-tight border-l-2 border-[#C5A038]/60 pl-3">
              <span className="text-[11px] md:text-xs font-semibold tracking-wide text-[#12532c] uppercase" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t({ en: 'Trusted for Value', ar: 'موثوقون في القيمة' })}
              </span>
              <span className="text-[11px] md:text-xs font-semibold tracking-wide text-[#C5A038] uppercase" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t({ en: 'Remembered for Integrity', ar: 'نُذكر بالنزاهة' })}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-gray-800 hover:text-[#12532c] transition-all duration-200 font-semibold text-base px-4 py-2 rounded-lg hover:bg-gray-100"
              >
                {t(item.label)}
              </Link>
            ))}

            {/* Language Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center space-x-2 border-gray-300 text-gray-800 hover:bg-gray-100"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'العربية' : 'English'}</span>
            </Button>

            {/* CTA Button */}
            <Button
              onClick={() => navigate('/contact')}
              className="bg-[#C5A038] hover:bg-[#b08f2f] text-white"
            >
              {t({ en: 'Get Started', ar: 'ابدأ الآن' })}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t(item.label)}
              </Link>
            ))}
            <div className="flex items-center space-x-3 px-4 py-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center space-x-2 flex-1"
              >
                <Globe className="w-4 h-4" />
                <span>{language === 'en' ? 'العربية' : 'English'}</span>
              </Button>
              <Button
                onClick={() => {
                  navigate('/contact');
                  setIsMobileMenuOpen(false);
                }}
                className="bg-[#C5A038] hover:bg-[#b08f2f] text-white flex-1"
              >
                {t({ en: 'Get Started', ar: 'ابدأ الآن' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
